import { getCookie } from '../../utils/helpers';
import request, { ResponseError } from '../../utils/request';

const API_BASE_URL =
  process.env.REACT_APP_API_V2_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/product-roadmap/api/v2'
    : '/product-roadmap/api/v2');

const SAVED_VIEWS_PATH = `${API_BASE_URL}/users/me/saved-views`;

const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

export interface SavedView {
  id: string;
  name: string;
  description?: string | null;
  customViewLink: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSavedViewRequest {
  name: string;
  description?: string;
  customViewLink: string;
}

export interface UpdateSavedViewRequest {
  name?: string;
  description?: string;
}

export function buildUpdateSavedViewPayload(
  original: Pick<SavedView, 'name' | 'description'>,
  updates: {
    name: string;
    description?: string;
  }
): UpdateSavedViewRequest {
  const payload: UpdateSavedViewRequest = {};

  const trimmedName = updates.name.trim();
  const trimmedDescription = (updates.description ?? '').trim();
  const originalDescription = (original.description ?? '').trim();

  if (trimmedName !== original.name) {
    payload.name = trimmedName;
  }

  if (trimmedDescription !== originalDescription) {
    payload.description = trimmedDescription || undefined;
  }

  return payload;
}

export class SavedViewApiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'SavedViewApiError';
  }
}

function extractMessageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const candidates = [record.message, record.error, record.detail];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }
  }
  return fallback;
}

async function parseErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const body = await response.json();
    return extractMessageFromBody(body, fallback);
  } catch {
    return fallback;
  }
}

function isSavedView(value: unknown): value is SavedView {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as SavedView).id === 'string' &&
    typeof (value as SavedView).name === 'string' &&
    typeof (value as SavedView).customViewLink === 'string'
  );
}

async function savedViewsRequest<T>(
  url: string,
  options: RequestInit,
  fallbackError: string,
  validate?: (result: unknown) => result is T
): Promise<T> {
  try {
    const result = await request(url, options, { showCustomError: true });

    if (validate && !validate(result)) {
      throw new SavedViewApiError(
        extractMessageFromBody(result, fallbackError),
        400
      );
    }

    return result as T;
  } catch (error) {
    if (error instanceof SavedViewApiError) {
      throw error;
    }
    if (error instanceof ResponseError) {
      const message = await parseErrorMessage(error.response, fallbackError);
      throw new SavedViewApiError(message, error.response.status);
    }
    throw error;
  }
}

export const listSavedViews = async (): Promise<SavedView[]> => {
  return savedViewsRequest(
    SAVED_VIEWS_PATH,
    {
      method: 'GET',
      headers: getAuthHeaders()
    },
    'Failed to load saved views',
    (result): result is SavedView[] => Array.isArray(result)
  );
};

export const createSavedView = async (
  body: CreateSavedViewRequest
): Promise<SavedView> => {
  return savedViewsRequest(
    SAVED_VIEWS_PATH,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    },
    'Failed to save view',
    isSavedView
  );
};

export const deleteSavedView = async (viewId: string): Promise<void> => {
  await savedViewsRequest(
    `${SAVED_VIEWS_PATH}/${viewId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders()
    },
    'Failed to delete view'
  );
};

export const updateSavedView = async (
  viewId: string,
  body: UpdateSavedViewRequest
): Promise<SavedView> => {
  return savedViewsRequest(
    `${SAVED_VIEWS_PATH}/${viewId}`,
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    },
    'Failed to update view',
    isSavedView
  );
};
